import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import ManagerPastPlacementsTable from './ManagerPastPlacementsTable';
import ManagerPastPlacementsGraph from './ManagerPastPlacementsGraph';
import ErrorBanner from '../UI/ErrorBanner';
import SegmentedControl from '../UI/SegmentedControl';
import { ManagerPastPlacementsProps, SeasonData } from '../../types';
import { useTeamContext } from '../../contexts/TeamContext';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const ManagerPastPlacements: React.FC<ManagerPastPlacementsProps> = ({ managerId}) => {
    const [pastRankings, setPastRankings] = useState<SeasonData[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [view, setView] = useState<'graph' | 'table'>('graph');
    const {leagueId} = useTeamContext();

    const toggleAccordion = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    useEffect(() => {
        const fetchPastRankings = async () => {
            try {
                setLoading(true);
                const response = await axios.get<SeasonData[]>(`${API_BASE_URL}/api/league/${leagueId}/pastRankings`);
                const sortedData = response.data.sort((a, b) => {
                    const yearA = parseInt(a.season_name.split('/')[0], 10);
                    const yearB = parseInt(b.season_name.split('/')[0], 10);
                    return yearB - yearA;
                });

                setPastRankings(sortedData);
                setLoading(false);
            } catch (err) {
                console.error(err);
                setError('Failed to fetch standings');
                setLoading(false);
            }
        };

        fetchPastRankings();
    }, [managerId, leagueId]);

    const isGraph = view === 'graph';

    return (
        <div className='bg-surface text-content mx-4 p-4 rounded-lg'>
            <h1 className='text-center text-2xl font-bold'>Rankings</h1>
            <p className='text-center mb-8 text-content-secondary'>See how you compare against the other managers in your league across the years!</p>

            {/* Tab Buttons */}
            <div className="flex justify-center mb-8">
                <SegmentedControl
                    options={[
                        { value: 'graph', label: 'Graph View' },
                        { value: 'table', label: 'Table View' },
                    ]}
                    value={view}
                    onChange={setView}
                />
            </div>

            {error && <ErrorBanner message={error} className="mb-4" />}
            {loading ? (
                <div className="flex justify-center items-center w-full h-full">
                    <LoadingSpinner />
                </div>
            ) : (
              <div className="relative w-full min-h-[400px]">
    <AnimatePresence mode="wait" initial={false}>
        <motion.div
            key={view}
            initial={{ rotateY: 90, opacity: 0 }}
            animate={{ rotateY: 0, opacity: 1 }}
            exit={{ rotateY: -90, opacity: 0 }}
            transition={{ duration: 0.6, ease: 'easeInOut' }}
            className="w-full overflow-hidden"
            style={{
                transformStyle: 'preserve-3d',
                backfaceVisibility: 'hidden',
            }}
        >
            {isGraph ? (
                <ManagerPastPlacementsGraph data={pastRankings} managerId={managerId} />
            ) : (
                <div className="space-y-4">
                    {pastRankings.map((season, index) => (
                        <div
                            key={season.season_name}
                            className="border border-line rounded-lg bg-surface-raised"
                        >
                            <button
                                onClick={() => toggleAccordion(index)}
                                aria-expanded={openIndex === index}
                                className="w-full min-h-11 p-4 text-left flex justify-between items-center cursor-pointer hover:bg-surface-sunken transition-colors font-semibold"
                            >
                                <span>{season.season_name} Season Rankings</span>
                                <ChevronDown
                                    size={18}
                                    className={`transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}
                                />
                            </button>

                            <AnimatePresence initial={false}>
                                {openIndex === index && (
                                    <motion.div
                                        key="content"
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                                        className="overflow-hidden border-t border-line"
                                    >
                                        <div className="p-4">
                                            <ManagerPastPlacementsTable season={season} managerId={managerId} />
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ))}
                </div>
            )}
        </motion.div>
    </AnimatePresence>
</div>

            )}
        </div>
    );
};

export default ManagerPastPlacements;
